import { ApplicationRef, EmbeddedViewRef, inject, Injectable, Injector } from '@angular/core';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Map, Popup } from 'maplibre-gl';
import { SimulationPopupComponent } from '../../components/simulation/simulation-popup/simulation-popup.component';
import {
  Feature,
  FeatureCollection,
  GeoJSON,
  Geometry as GeoJSONGeometry,
} from 'geojson';
import * as turf from '@turf/turf';
import { Store } from '@ngrx/store';
import { selectExplorerSJMap } from '../../store/selectors/explorer/explorer-sjmap.selector';
import { Observable, take } from 'rxjs';
import {
  addAnalyticsData,
  cleanAnalyticsData,
} from '../../store/actions/explorer/explorer-analytics-tool.actions';
import { BehaviorSubject } from 'rxjs';
import { ComponentRef, createComponent } from '@angular/core';
import { SimulationMasterplan } from '../../interfaces/simulation/ISimulation';
import { ToastrService } from '../toastr.service';
import { saveSimulationArea } from '../../store/actions/simulation-area/simulation-area.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ISimulationArea } from '../../interfaces/simulation/simulation-area/ISimulationArea';
import {
  buildSimulationMask,
  fcFitsMask,
  toFeatureCollection,
} from '../../utility/simulation-geo.utils';
import { IUserLicense } from '../../interfaces/licenses/IUserLicense';
import { selectUserLicense } from '../../store/selectors/licenses/user-license/user-license.selectors';
import { AnalyticsToolService } from '../explorer/analytics-tool/analytics-tool.service';

@Injectable({
  providedIn: 'root',
})
export class SimulationDrawClassesService {
  private store = inject(Store);
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private analyticsToolService = inject(AnalyticsToolService);
  private draw!: MapboxDraw;
  private map: Map | null = null;
  private splitMap: Map | null = null;
  private splitDraw!: MapboxDraw;

  selectUserLicense$: Observable<IUserLicense> =
    this.store.select(selectUserLicense);

  availableSimulationAreaKm2: number;

  saveSimulationDisabled$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  popup = new Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '450px',
  });


  private popupComponentRef: ComponentRef<SimulationPopupComponent> | null =
    null;
  private popupCloseBound = false;
  private splitPopup: Popup | null = null;
  private splitPopupComponentRef: ComponentRef<SimulationPopupComponent> | null =
    null;

  isDrawing: BehaviorSubject<{
    status: boolean;
    layerId: string | null;
    analytics: boolean;
    area: boolean;
    featureId: string | null;
  }> = new BehaviorSubject<{
    status: boolean;
    layerId: string | null;
    analytics: boolean;
    area: boolean;
    featureId: string | null;
  }>({
    status: false,
    layerId: null,
    analytics: false,
    area: false,
    featureId: null,
  });

  private selectedInterventionId: number | null = null;
  private toastrService = inject(ToastrService);
  private modalService: NgbModal = inject(NgbModal);

  private previousFeatureStates: Record<string, Feature> = {};
  private newlyCreatedFeatureIds = new Set<string>();

  hasMasterplan$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  constructor() {
    this.selectUserLicense$.subscribe((license) => {
      this.availableSimulationAreaKm2 = license?.available_simulation_area_km2;
    });
  }
  setMap(map: Map) {
    this.destroyPopupComponent(this.popupComponentRef);
    this.popupComponentRef = null;
    this.popup.remove();
    this.map = map;
  }
  setDraw(draw: MapboxDraw) {
    this.draw = draw;
    if (this.map && this.draw) {
      this.map.on('draw.update', (e) => this.onDrawUpdate(e));
      this.map.on('draw.selectionchange', (e) => this.onDrawSelectionChange(e));
    }
  }

  setInterventionId(value: number | null) {
    this.selectedInterventionId = value;
  }

  setSplitMap(splitMap: Map) {
    this.destroyPopupComponent(this.splitPopupComponentRef);
    this.splitPopupComponentRef = null;
    this.splitPopup?.remove();
    this.splitPopup = null;
    this.splitMap = splitMap;
  }
  setSplitDraw(splitDraw: MapboxDraw) {
    this.splitDraw = splitDraw;
  }

  enableDrawSimulation(
    mode: 'point' | 'line' | 'line_string' | 'polygon',
    layerId: string = null,
    area: boolean = null,
  ): void {
    this.isDrawing.next({
      status: true,
      layerId,
      analytics: false,
      area: area,
      featureId: null,
    });

    this.draw.changeMode(`draw_${mode}`);
    // this.map.once('click', this.boundOnMapClickForComment);
  }

  disableSecondaryEvent() {
    this.isDrawing.next({
      status: false,
      layerId: null,
      analytics: true,
      area: false,
      featureId: null,
    });
  }

  disableDrawSimulation() {
    this.isDrawing.next({
      status: false,
      layerId: null,
      analytics: false,
      area: false,
      featureId: null,
    });
  }

  onDrawCreate(e: { features: Feature[] }): void {
    const featureId: string = e.features[0].id as string;
    const newFeature = e.features[0];
    this.newlyCreatedFeatureIds.add(featureId);

    // Solo per masterplan (non analytics)

    // Controllo per l'overlap per le feature di simulazione
    // if (!this.isDrawing.value.analytics && !this.isDrawing.value.area) {
    //   const existingFeatures =
    //     this.getSimulationFeaturesAsFeatureCollection().features;

    //   let isOverlapping: boolean = false; // Checks the overlapping with the masterplan features
    //   if (newFeature.geometry.type === 'Polygon') {
    //     isOverlapping = existingFeatures.some(
    //       (f) =>
    //         f.id !== featureId &&
    //         f.geometry.type === 'Polygon' &&
    //         turf.booleanIntersects(f, newFeature)
    //     );
    //   } else if (newFeature.geometry.type === 'LineString') {
    //     isOverlapping = existingFeatures.some(
    //       (f) =>
    //         f.id !== featureId &&
    //         f.geometry.type === 'LineString' &&
    //         turf.booleanIntersects(f, newFeature)
    //     );
    //   } else if (newFeature.geometry.type === 'Point') {
    //     isOverlapping = existingFeatures.some(
    //       (f) =>
    //         f.id !== featureId &&
    //         f.geometry.type === 'Point' &&
    //         turf.booleanEqual(f, newFeature)
    //     );
    //   }
    //   if (isOverlapping) {
    //     let errorMessage = '';
    //     errorMessage =
    //       'You cannot overlay or duplicate one simulation drawing onto another.';
    //     this.toastrService.show('Errore', errorMessage, 'error');
    //     this.draw.delete(featureId);
    //     this.isDrawing.next({
    //       status: false,
    //       layerId: null,
    //       analytics: false,
    //       area: false,
    //       featureId: null,
    //     });
    //     return;
    //   }
    // }
    if (
      featureId &&
      this.selectedInterventionId !== null &&
      !this.isDrawing.value.area
    ) {
      this.draw.setFeatureProperty(
        featureId,
        'intervention',
        this.selectedInterventionId,
      );
      this.selectedInterventionId = null;
    }
    if (this.isDrawing.value.analytics && !this.isDrawing.value.area) {
      // this.store.dispatch(
      //   addAnalyticsData({
      //     layerUUID: this.isDrawing.value.layerId,
      //     name: featureId,
      //   })
      // );
      this.draw.setFeatureProperty(featureId, 'analytics', true);
    }

    if (this.isDrawing.value.area) {
      this.draw.setFeatureProperty(featureId, 'simulationArea', true);

      // Limits the area to 50km^2
      let areaInKm2 = null;
      if (
        newFeature.geometry.type === 'Polygon' ||
        newFeature.geometry.type === 'MultiPolygon'
      ) {
        const area = turf.area(newFeature);
        areaInKm2 = area / 1_000_000;
      }

      if (areaInKm2 > 75) {
        this.toastrService.show(
          'Warning',
          'The Simulation Area cannot be larger than 50km^2.',
          'warning',
        );
        this.draw.delete(featureId);
        this.isDrawing.next({
          status: false,
          layerId: null,
          analytics: false,
          area: false,
          featureId: featureId,
        });
      } else if (areaInKm2 > this.availableSimulationAreaKm2) {
        this.toastrService.show(
          'Warning',
          'The Simulation Area cannot be larger than ' +
            this.availableSimulationAreaKm2 +
            ' km^2.',
          'warning',
        );
        this.draw.delete(featureId);
        this.isDrawing.next({
          status: false,
          layerId: null,
          analytics: false,
          area: false,
          featureId: featureId,
        });
      } else {
        if (!this.isSimulationAreaValidForMasterplan(newFeature)) {
          this.toastrService.show(
            'Error',
            'The simulation features must be contained within the Simulation Area',
            'error',
          );
          this.draw.delete(featureId);
          this.isDrawing.next({
            status: false,
            layerId: null,
            analytics: false,
            area: false,
            featureId: featureId,
          });
          return;
        }

        this.saveSimulationDisabled$.next(false);
      }
    }

    this.isDrawing.next({
      status: false,
      layerId: null,
      analytics: this.isDrawing.value.analytics ? true : false,
      area: false,
      featureId: featureId,
    });

    if (!this.isDrawing.value.analytics) {
      this.recomputeHasMasterplan();
    }
  }

  saveSimulationAreaModal(simulationAreaName: string) {
    // Salvataggio area di simulazione
    const featureId = this.isDrawing.value.featureId;
    const feature = this.draw.get(featureId);

    this.draw.delete(featureId);

    this.store.dispatch(
      saveSimulationArea({
        name: simulationAreaName,
        feature: feature,
        areaInKm2: turf.area(feature) / 1_000_000,
      }),
    );

    this.modalService.dismissAll();
  }

  // Salva lo stato precedente della feature selezionata
  onDrawSelectionChange(e: { features: Feature[] }): void {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      this.previousFeatureStates[feature.id] = JSON.parse(
        JSON.stringify(feature),
      );
    }
  }

  onDrawUpdate(e: { features: Feature[] }): void {
    const featureId: string = e.features[0].id as string;
    const updatedFeature = e.features[0];

    const prev = this.previousFeatureStates[featureId];
    // console.log(prev);
    let isOverlappingArea: boolean = false;
    let errorMessage = '';
    let existingFeatures =
      this.getSimulationFeaturesAsFeatureCollection().features;

    // Controllo per l'overlap per le feature di simulazione
    // if (
    //   !this.isDrawing.value.analytics &&
    //   !this.isDrawing.value.area &&
    //   !updatedFeature.properties.simulationArea &&
    //   !updatedFeature.properties.analyticsData &&
    //   !updatedFeature.properties.analytics
    // ) {
    // let isOverlapping: boolean = false;
    // if (updatedFeature.geometry.type === 'Polygon') {
    //   isOverlapping = existingFeatures.some(
    //     (f) =>
    //       f.id !== featureId &&
    //       f.geometry.type === 'Polygon' &&
    //       turf.booleanIntersects(f, updatedFeature)
    //   );
    // } else if (updatedFeature.geometry.type === 'LineString') {
    //   isOverlapping = existingFeatures.some(
    //     (f) =>
    //       f.id !== featureId &&
    //       f.geometry.type === 'LineString' &&
    //       turf.booleanIntersects(f, updatedFeature)
    //   );
    // } else if (updatedFeature.geometry.type === 'Point') {
    //   isOverlapping = existingFeatures.some(
    //     (f) =>
    //       f.id !== featureId &&
    //       f.geometry.type === 'Point' &&
    //       turf.booleanEqual(f, updatedFeature)
    //   );
    // }
    // if (isOverlapping) {
    //   errorMessage =
    //     'You cannot overlay or duplicate one simulation drawing onto another.';
    //   this.toastrService.show('Errore', errorMessage, 'error');
    //   // Ripristina la feature alla posizione precedente
    //   if (prev) {
    //     this.draw.delete(featureId);
    //     this.draw.add(prev);
    //   }
    //   this.isDrawing.next({
    //     status: false,
    //     layerId: null,
    //     analytics: false,
    //     area: false,
    //     featureId: null,
    //   });
    //   return;
    // }
    // }

    if (
      updatedFeature.properties?.['simulationArea'] &&
      !this.isDrawing.value.analytics
    ) {
      existingFeatures =
        this.getSimulationFeaturesAsFeatureCollection().features;
      isOverlappingArea = this.isSimulationAreaValidForMasterplan(
        updatedFeature,
      );

      let areaInKm2 = null;
      if (
        updatedFeature.geometry.type === 'Polygon' ||
        updatedFeature.geometry.type === 'MultiPolygon'
      ) {
        const area = turf.area(updatedFeature);
        areaInKm2 = area / 1_000_000;
      }

      if (!isOverlappingArea && existingFeatures.length > 0) {
        errorMessage =
          'The simulation features must be contained within the Simulation Area';
        this.toastrService.show('Error', errorMessage, 'error');
        // Ripristina la feature alla posizione precedente
        if (prev) {
          this.draw.delete(featureId);
          this.draw.add(prev);
        }
        this.isDrawing.next({
          status: false,
          layerId: null,
          analytics: false,
          area: false,
          featureId: null,
        });
        return;
      }

      if (areaInKm2 > 75) {
        this.toastrService.show(
          'Error',
          'The Simulation Area cannot be larger than 75km^2.',
          'error',
        );
        this.draw.delete(featureId);
        const prev = this.previousFeatureStates[featureId];
        if (prev) {
          this.draw.add(prev);
        }
        this.isDrawing.next({
          status: false,
          layerId: null,
          analytics: false,
          area: false,
          featureId: featureId,
        });
        return;
      }
    }
    this.recomputeHasMasterplan();
  }

  getSimulationFeaturesAsFeatureCollection(
    featureType?: string,
  ): GeoJSON.FeatureCollection {
    if (!this.draw) {
      // console.warn('Draw non inizializzato');
      return {
        type: 'FeatureCollection',
        features: [],
        crs: {
          type: 'name',
          properties: {
            name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
          },
        },
      } as GeoJSON.FeatureCollection;
    }
    const allFeatures = this.draw.getAll();
    let simulationFeatures: Feature[];
    // console.log(allFeatures.features);

    if (!featureType) {
      simulationFeatures = allFeatures.features.filter((f) => {
        return (
          !f.properties?.['analyticsData'] &&
          !f.properties?.['simulationArea'] &&
          !f.properties?.['analytics']
        );
      });
    } else if (featureType === 'simulationArea') {
      simulationFeatures = allFeatures.features.filter((f) => {
        return f.properties?.['simulationArea'];
      });
    }
    return {
      type: 'FeatureCollection',
      features: simulationFeatures,
      crs: {
        type: 'name',
        properties: {
          name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
        },
      },
    } as GeoJSON.FeatureCollection;
  }

  getSimulationDraw(): MapboxDraw {
    return this.draw;
  }
  private async maplibreRasterLayerIdToSJMapUUID(
    layerId: string,
  ): Promise<string | null> {
    return new Promise((resolve) => {
      this.store
        .select(selectExplorerSJMap)
        .pipe(take(1))
        .subscribe((sjmapState) => {
          const allLayers = sjmapState?.sjmap?.layers || ([] as any[]);
          for (const layer of allLayers) {
            if (layer?.type !== 'raster') continue;
            const sources = (layer as any).sources || [];
            for (const src of sources) {
              const ml = src?.maplibre;
              if (ml && typeof ml.layer === 'string' && ml.layer === layerId) {
                resolve(layer.uuid);
                return;
              }
            }
          }
          resolve(null);
        });
    });
  }

  private async resolveTopVisibleRasterUUID(): Promise<string | null> {
    const map = this.map;
    if (!map) return null;
    const style = map.getStyle();
    const layers = (style?.layers || []).slice().reverse();
    for (const layer of layers) {
      if (layer.type !== 'raster') continue;
      const id = layer.id;
      if (!id || id === 'mapbox-xyz' || id === 'basemap-layer') continue;
      const visibility = map.getLayoutProperty(id, 'visibility');
      if (visibility === 'none') continue;
      const uuid = await this.maplibreRasterLayerIdToSJMapUUID(id);
      if (uuid) return uuid;
    }
    return null;
  }

  async addGeoJSONToDrawAnalytics(geojson: GeoJSON): Promise<void> {
    if (this.map.getLayer('uploaded-geojson-style')) {
      this.map.removeLayer('uploaded-geojson-style');
    }
    if (this.map.getSource('uploaded-geojson-source')) {
      this.map.removeSource('uploaded-geojson-source');
    }
    this.map.addSource('uploaded-geojson-source', {
      type: 'geojson',
      data: geojson,
    });

    // Resolve target raster UUID once at import time
    const targetLayerUUID = await this.resolveTopVisibleRasterUUID();

    if (geojson.type === 'FeatureCollection') {
      for (const feature of geojson.features) {
        const res = this.draw.add(feature);
        const newId = Array.isArray(res) ? res[0] : res;
        if (targetLayerUUID && typeof newId === 'string') {
          this.store.dispatch(
            addAnalyticsData({ layerUUID: targetLayerUUID, name: newId }),
          );
        }
      }
    } else if (geojson.type === 'Feature') {
      const res = this.draw.add(geojson);
      const newId = Array.isArray(res) ? res[0] : res;
      if (targetLayerUUID && typeof newId === 'string') {
        this.store.dispatch(
          addAnalyticsData({ layerUUID: targetLayerUUID, name: newId }),
        );
      }
    } else {
      // console.warn('Unsupported GeoJSON type for drawing:', geojson);
    }

    // Signal bottom bar open after features are on map
    this.analyticsToolService.triggerOpenBottomBar();
    this.recomputeHasMasterplan();
  }
  addGeoJSONToDraw(geojson: GeoJSON): void {
    if (!this.draw || !this.map) return;

    const interventionColors: Record<string, string> = {
      '1': '#ff0000',
      '2': '#b3b3b3',
      '3': '#666666',
      '4': '#ffff00',
      '5': '#00ff00',
      '6': '#008000',
      '7': '#00b300',
      '8': '#006600',
      '9': '#ff8000',
      '10': '#ff00ff',
      '11': '#0000ff',
    };

    const simAreaFC =
      this.getSimulationFeaturesAsFeatureCollection('simulationArea');
    const simAreas = simAreaFC.features;
    const shouldValidateAgainstSimulationArea = this.hasMasterplan$.value;
    const fc = toFeatureCollection(geojson as FeatureCollection);
    const simulationMask = simAreas.length
      ? buildSimulationMask(simAreas)
      : null;

    if (fc.type === 'FeatureCollection') {
      for (const feature of fc.features) {
        this.draw.add(feature);
      }
    } else if (fc.type === 'Feature') {
      this.draw.add(fc);
    }

    if (this.map.getLayer('uploaded-geojson-style')) {
      this.map.removeLayer('uploaded-geojson-style');
    }
    if (this.map.getSource('uploaded-geojson-source')) {
      this.map.removeSource('uploaded-geojson-source');
    }

    this.map.addSource('uploaded-geojson-source', {
      type: 'geojson',
      data: geojson,
    });
    this.map.addLayer({
      id: 'uploaded-geojson-style',
      type: 'fill',
      source: 'uploaded-geojson-source',
      paint: {
        'fill-color': [
          'match',
          ['to-string', ['get', 'intervention']],
          '1',
          interventionColors['1'],
          '2',
          interventionColors['2'],
          '3',
          interventionColors['3'],
          '4',
          interventionColors['4'],
          '5',
          interventionColors['5'],
          '6',
          interventionColors['6'],
          '7',
          interventionColors['7'],
          '8',
          interventionColors['8'],
          '9',
          interventionColors['9'],
          '10',
          interventionColors['10'],
          '11',
          interventionColors['11'],
          '#cccccc',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': '#222',
      },
      layout: {},
      filter: ['==', '$type', 'Polygon'],
    });

    this.recomputeHasMasterplan();
  }

  addGeoJSONSimulationAreaToDraw(geojson: GeoJSON): void {
    if (!this.draw || !this.map) return;

    const existingSimulationArea =
      this.getSimulationFeaturesAsFeatureCollection('simulationArea').features;

    if (existingSimulationArea.length > 0) return;

    if (!this.isSimulationAreaValidForMasterplan(geojson as GeoJSON)) {
      this.toastrService.show(
        'warning',
        'The simulation features must be contained within the Simulation Area',
        'warning',
      );
      return;
    }

    if (geojson.type === 'FeatureCollection') {
      for (const feature of geojson.features) {
        const res = this.draw.add(feature);
        this.isDrawing.next({
          status: false,
          layerId: null,
          analytics: this.isDrawing.value.analytics ? true : false,
          area: false,
          featureId: res[0],
        });
        this.saveSimulationDisabled$.next(false);
      }
    } else if (geojson.type === 'Feature') {
      const res = this.draw.add(geojson);
      this.isDrawing.next({
        status: false,
        layerId: null,
        analytics: this.isDrawing.value.analytics ? true : false,
        area: false,
        featureId: res[0],
      });
      this.saveSimulationDisabled$.next(false);
    }
  }

  loadSimulationAreaOnMap(
    simulationAreaGeometry: ISimulationArea,
    previousSimulationAreaUuid: Array<string>,
  ) {
    if (previousSimulationAreaUuid) {
      this.draw.delete(previousSimulationAreaUuid);
    }
    const normalizedSimulationArea = this.normalizeSimulationAreaGeoJSON(
      simulationAreaGeometry.feature as GeoJSON,
    );
    const result = this.draw.add(normalizedSimulationArea);

    this.draw.setFeatureProperty(result[0], 'simulationArea', true);
    return result;
  }

  private destroyPopupComponent(
    ref: ComponentRef<SimulationPopupComponent> | null,
  ): void {
    if (!ref) return;
    this.appRef.detachView(ref.hostView);
    ref.destroy();
  }

  openSimulationPopup(
    lngLat: [number, number],
    feature: GeoJSON.Feature,
    featureIndex: number,
  ) {
    if (!this.map) return;

    this.destroyPopupComponent(this.popupComponentRef);
    this.popupComponentRef = null;
    this.popup.remove();
    if (!this.popupCloseBound) {
      this.popup.on('close', () => {
        this.destroyPopupComponent(this.popupComponentRef);
        this.popupComponentRef = null;
      });
      this.popupCloseBound = true;
    }

    const isNewlyDrawn = this.newlyCreatedFeatureIds.has(feature.id as string);

    const container = document.createElement('div');
    container.classList.add('simulation-popup');
    const componentRef: ComponentRef<SimulationPopupComponent> =
      createComponent(SimulationPopupComponent, {
        environmentInjector: this.appRef.injector,
        elementInjector: this.injector,
      });
    componentRef.instance.isEditing =
      localStorage.getItem('masterplanUUID') !== null;
    componentRef.instance.featureProperties = feature;
    componentRef.instance.centroid = lngLat;
    componentRef.instance.geometryType = feature.geometry.type;
    componentRef.instance.featureIndex = featureIndex;

    componentRef.instance.isNewlyDrawn = isNewlyDrawn;
    let areaInKm2 = null;
    if (
      feature.geometry.type === 'Polygon' ||
      feature.geometry.type === 'MultiPolygon'
    ) {
      const area = turf.area(feature);
      areaInKm2 = area / 1_000_000;
    }
    componentRef.instance.areaInKm2 = areaInKm2;
    this.appRef.attachView(componentRef.hostView);
    container.appendChild((componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0]);
    this.popupComponentRef = componentRef;
    this.popup.setDOMContent(container).setLngLat(lngLat).addTo(this.map);
  }

  openSimulationPopupOnSplitMap(
    lngLat: [number, number],
    feature: GeoJSON.Feature,
    featureIndex: number,
  ) {
    if (!this.splitMap) return;

    this.destroyPopupComponent(this.splitPopupComponentRef);
    this.splitPopupComponentRef = null;
    this.splitPopup?.remove();
    this.splitPopup = null;

    const isNewlyDrawn = this.newlyCreatedFeatureIds.has(feature.id as string);

    const container = document.createElement('div');
    container.classList.add('simulation-popup');
    const componentRef: ComponentRef<SimulationPopupComponent> =
      createComponent(SimulationPopupComponent, {
        environmentInjector: this.appRef.injector,
        elementInjector: this.injector,
      });
    componentRef.instance.isEditing =
      localStorage.getItem('masterplanUUID') !== null;
    componentRef.instance.featureProperties = feature;
    componentRef.instance.centroid = lngLat;
    componentRef.instance.geometryType = feature.geometry.type;
    componentRef.instance.featureIndex = featureIndex;

    componentRef.instance.isNewlyDrawn = isNewlyDrawn;
    let areaInKm2 = null;
    if (
      feature.geometry.type === 'Polygon' ||
      feature.geometry.type === 'MultiPolygon'
    ) {
      const area = turf.area(feature);
      areaInKm2 = area / 1_000_000;
    }
    componentRef.instance.areaInKm2 = areaInKm2;
    this.appRef.attachView(componentRef.hostView);
    container.appendChild((componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0]);

    // Crea un popup separato per la split map
    const splitMapPopup = new Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '450px',
    });
    this.splitPopup = splitMapPopup;
    this.splitPopupComponentRef = componentRef;
    splitMapPopup.on('close', () => {
      this.destroyPopupComponent(this.splitPopupComponentRef);
      this.splitPopupComponentRef = null;
      this.splitPopup = null;
    });

    splitMapPopup
      .setDOMContent(container)
      .setLngLat(lngLat)
      .addTo(this.splitMap);
  }

  deleteDrawSimulation(featureId: string): void {
    if (!this.draw || !featureId) return;
    this.draw.delete(featureId);
    this.popup.remove();

    this.newlyCreatedFeatureIds.delete(featureId);

    this.store.dispatch(cleanAnalyticsData({ featureId }));

    this.saveSimulationDisabled$.next(!this.hasAnySimulationArea());

    this.recomputeHasMasterplan();
  }

  deleteSpecificFeature(simulationAreaUuid: Array<string>) {
    if (!this.draw || !simulationAreaUuid?.length) return;
    this.draw.delete(simulationAreaUuid);
    this.saveSimulationDisabled$.next(!this.hasAnySimulationArea());
    this.recomputeHasMasterplan();
  }

  deleteSimulationAreaFeatures() {
    if (!this.draw) return;
    const simulationAreaIds = this.getSimulationFeaturesAsFeatureCollection(
      'simulationArea',
    )
      .features.map((feature) => String(feature.id))
      .filter(Boolean);

    if (!simulationAreaIds.length) return;

    this.draw.delete(simulationAreaIds);
    this.saveSimulationDisabled$.next(true);
    this.recomputeHasMasterplan();
  }

  deleteAllFeatures() {
    this.draw.deleteAll();
    if (this.map.getLayer('uploaded-geojson-style')) {
      this.map.removeLayer('uploaded-geojson-style');
    }
    this.saveSimulationDisabled$.next(true);
    this.recomputeHasMasterplan();
  }

  bindSimulationPopupOnLayer(sim: SimulationMasterplan, layerId: string): void {
    const map = this.map;
    if (!map) return;
    map.on('click', layerId, (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const featureIndex = 1;
      this.openSimulationPopup(lngLat, feature, featureIndex);
    });
    map.on('mouseenter', layerId, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', layerId, () => {
      map.getCanvas().style.cursor = '';
    });
  }

  /**
   * Bind click su tutti i layer simulation (fill, line, point) per aprire solo il SimulationPopupComponent
   */
  bindSimulationPopupOnAllLayers(sim: SimulationMasterplan, layerId: string) {
    if (!this.map) return;
    const layers = [layerId, `${layerId}-line`, `${layerId}-point`];
    layers.forEach((l) => {
      this.map.on('click', l, (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        if (feature.properties && 'intervention' in feature.properties) {
          const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          const featureIndex = 1;
          this.openSimulationPopup(lngLat, feature, featureIndex);
        }
      });
      this.map.on('mouseenter', l, () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', l, () => {
        this.map.getCanvas().style.cursor = '';
      });
    });
  }

  bindSimulationPopupOnSplitMapLayers(
    sim: SimulationMasterplan,
    layerId: string,
  ) {
    if (!this.splitMap) return;
    const layers = [layerId, `${layerId}-line`, `${layerId}-point`];
    layers.forEach((l) => {
      this.splitMap.on('click', l, (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        if (feature.properties && 'intervention' in feature.properties) {
          const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          const featureIndex = 1;
          this.openSimulationPopupOnSplitMap(lngLat, feature, featureIndex);
        }
      });
      this.splitMap.on('mouseenter', l, () => {
        this.splitMap.getCanvas().style.cursor = 'pointer';
      });
      this.splitMap.on('mouseleave', l, () => {
        this.splitMap.getCanvas().style.cursor = '';
      });
    });
  }

  /**
   * Bind click su tutte le feature draw per aprire il SimulationPopupComponent se la feature ha intervention
   */
  bindDrawFeatureClickPopup() {
    if (!this.map) return;
    this.map.on('click', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: this.map
          .getStyle()
          .layers.filter(
            (l) =>
              l.id.startsWith('gl-draw') &&
              !l.id.includes('midpoint') &&
              !l.id.includes('vertex'),
          )
          .map((l) => l.id),
      });

      if (!features.length) return;
      const feature = features[0];
      const featureId = feature.properties && feature.properties['id'];
      if (!featureId) return;
      const logicalFeature = this.draw.get(featureId);
      if (!logicalFeature) return;
      // Solo per simulation
      if (
        logicalFeature.properties &&
        'intervention' in logicalFeature.properties
      ) {
        const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const index = 1;
        this.openSimulationPopup(lngLat, logicalFeature, index);
        return;
      }
    });
  }

  /**
   * Bind click su tutte le feature draw della splitMap per aprire il SimulationPopupComponent se la feature ha intervention
   */
  bindDrawFeatureClickPopupOnSplitMap() {
    if (!this.splitMap) return;

    this.splitMap.on('click', (e) => {
      if (!this.splitDraw) return;

      const storeAlive = (this.splitDraw as  { ctx?: { store?: unknown } })?.ctx?.store;
      if (!storeAlive) return;

      const features = this.splitMap.queryRenderedFeatures(e.point, {
        layers: this.splitMap
          .getStyle()
          .layers.filter(
            (l) =>
              l.id.startsWith('gl-draw') &&
              !l.id.includes('midpoint') &&
              !l.id.includes('vertex'),
          )
          .map((l) => l.id),
      });
      if (!features.length) return;
      const feature = features[0];
      const featureId = feature.properties && feature.properties['id'];
      if (!featureId) return;
      const logicalFeature = this.splitDraw.get(featureId);
      if (!logicalFeature) return;
      // Solo per simulation
      if (
        logicalFeature.properties &&
        'intervention' in logicalFeature.properties
      ) {
        const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const index = 1;
        this.openSimulationPopup(lngLat, logicalFeature, index);
        return;
      }
    });
  }

  /**
   * Aggiunge tutte le feature masterplan al draw (per la modifica)
   */
  addMasterplanFeaturesToDraw(masterplanGeoJSON: GeoJSON) {
    if (!this.draw) return;
    if (masterplanGeoJSON.type === 'FeatureCollection') {
      for (const feature of masterplanGeoJSON.features) {
        this.draw.add(feature);
      }
    } else if (masterplanGeoJSON.type === 'Feature') {
      this.draw.add(masterplanGeoJSON);
    }
    this.recomputeHasMasterplan();
  }

  /**
   * Rimuove tutte le feature masterplan dal draw (usando la property 'intervention')
   */
  removeMasterplanFeaturesFromDraw() {
    if (!this.draw) return;
    const allFeatures = this.draw.getAll();
    const toRemove = allFeatures.features.filter(
      (f) => f.properties && 'intervention' in f.properties,
    );
    for (const feature of toRemove) {
      this.draw.delete(String(feature.id));
    }
    this.recomputeHasMasterplan();
  }

  removeMasterplan() {
    if (!this.draw) return;
    const allFeatures = this.draw.getAll();
    const toRemove = allFeatures.features.filter((f) => {
      return (
        !f.properties?.['analyticsData'] &&
        !f.properties?.['simulationArea'] &&
        !f.properties?.['analytics']
      );
    });

    for (const feature of toRemove) {
      this.draw.delete(String(feature.id));
    }

    if (this.map.getLayer('uploaded-geojson-style')) {
      this.map.removeLayer('uploaded-geojson-style');
    }
    if (this.map.getSource('uploaded-geojson-source')) {
      this.map.removeSource('uploaded-geojson-source');
    }

    this.recomputeHasMasterplan();
  }

  /**
   * Rimuove i layer custom masterplan dalla mappa (sim-<uuid>, sim-src-<uuid>, ecc.)
   * @param simUUIDs array di uuid dei masterplan
   */
  removeMasterplanCustomLayers(simUUIDs: string[]) {
    if (!this.map) return;
    for (const uuid of simUUIDs) {
      const layerIds = [`sim-${uuid}`, `sim-${uuid}-line`, `sim-${uuid}-point`];
      const sourceId = `sim-src-${uuid}`;
      for (const lid of layerIds) {
        if (this.map.getLayer(lid)) {
          this.map.removeLayer(lid);
        }
      }
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    }
  }
  exitDrawingSimulationArea(removeCurrentFeature = false) {
    try {
      this.draw?.changeMode?.('simple_select');
    } catch {}

    if (removeCurrentFeature) {
      const featureId = this.isDrawing.value.featureId;
      if (featureId) {
        this.deleteDrawSimulation(featureId);
      }
    }

    this.disableDrawSimulation();
    this.saveSimulationDisabled$.next(!this.hasAnySimulationArea());
  }
  private hasAnySimulationArea(): boolean {
    const fc = this.getSimulationFeaturesAsFeatureCollection('simulationArea');
    return Array.isArray(fc.features) && fc.features.length > 0;
  }

  isSimulationAreaValidForMasterplan(simulationAreaGeoJSON: GeoJSON): boolean {
    const masterplan = this.getSimulationFeaturesAsFeatureCollection();
    if (!masterplan.features.length) {
      return true;
    }

    const normalizedSimulationArea = this.normalizeSimulationAreaGeoJSON(
      simulationAreaGeoJSON,
    );
    const simulationArea = toFeatureCollection(normalizedSimulationArea as FeatureCollection);
    const mask = buildSimulationMask(simulationArea.features);

    if (!mask) {
      return false;
    }

    return fcFitsMask(masterplan, mask);
  }

  private normalizeSimulationAreaGeoJSON(
    simulationAreaGeoJSON: GeoJSON,
  ): GeoJSON | FeatureCollection {
    if (!simulationAreaGeoJSON) {
      return simulationAreaGeoJSON;
    }

    if (
      simulationAreaGeoJSON.type === 'Feature' ||
      simulationAreaGeoJSON.type === 'FeatureCollection'
    ) {
      return simulationAreaGeoJSON as GeoJSON;
    }

    return {
      type: 'Feature',
      geometry: simulationAreaGeoJSON as GeoJSONGeometry,
      properties: {
        simulationArea: true,
      },
    } as GeoJSON;
  }

  private recomputeHasMasterplan(): void {
    const fc = this.getSimulationFeaturesAsFeatureCollection();
    this.hasMasterplan$.next(
      Array.isArray(fc.features) && fc.features.length > 0,
    );
  }
}
